import { useEffect, useRef, useState, useCallback } from 'react';

interface Props {
  onResult: (value: string) => void;
  onClose: () => void;
}

type Mode = 'camera' | 'upload';

export default function QRScannerModal({ onResult, onClose }: Props) {
  const [mode, setMode] = useState<Mode>('camera');
  const [cameraError, setCameraError] = useState('');
  const [scanning, setScanning] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [uploadScanning, setUploadScanning] = useState(false);

  const scannerRef = useRef<import('html5-qrcode').Html5Qrcode | null>(null);
  const started = useRef(false);
  const stopped = useRef(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const stopCamera = useCallback(async () => {
    if (scannerRef.current && started.current) {
      try {
        await scannerRef.current.stop();
      } catch {
        // ignore stop errors
      }
    }
  }, []);

  useEffect(() => {
    if (mode !== 'camera') return;

    stopped.current = false;

    const initCamera = async () => {
      try {
        const { Html5Qrcode } = await import('html5-qrcode');
        const scanner = new Html5Qrcode('swifttrack-qr-reader');
        scannerRef.current = scanner;

        await scanner.start(
          { facingMode: 'environment' },
          { fps: 10, qrbox: { width: 230, height: 230 } },
          (decoded) => {
            if (stopped.current) return;
            stopped.current = true;
            scanner.stop().catch(() => {}).finally(() => onResult(decoded));
          },
          () => {}
        );

        if (!stopped.current) setScanning(true);
        started.current = true;
      } catch (err) {
        const msg = String(err).toLowerCase();
        if (msg.includes('permission') || msg.includes('denied') || msg.includes('notallowed')) {
          setCameraError('permission');
        } else if (msg.includes('notfound') || msg.includes('no camera') || msg.includes('device')) {
          setCameraError('no_camera');
        } else {
          setCameraError('generic');
        }
      }
    };

    initCamera();

    return () => {
      stopped.current = true;
      stopCamera();
    };
  }, [mode, onResult, stopCamera]);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadError('');
    setUploadScanning(true);

    try {
      const { Html5Qrcode } = await import('html5-qrcode');
      const scanner = new Html5Qrcode('swifttrack-file-scan-container');
      const result = await scanner.scanFile(file, false);
      scanner.clear().catch(() => {});
      onResult(result);
    } catch {
      setUploadError('No QR code found in this image. Please try a clearer photo or enter your tracking number manually.');
    } finally {
      setUploadScanning(false);
      // reset input so the same file can be re-selected
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const switchMode = async (next: Mode) => {
    if (next === mode) return;
    if (mode === 'camera') {
      stopped.current = true;
      await stopCamera();
      setScanning(false);
      setCameraError('');
      started.current = false;
    }
    setMode(next);
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.88)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-2xl w-full max-w-sm overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div
              className="w-9 h-9 rounded-xl flex items-center justify-center"
              style={{ background: 'var(--primary-light)' }}
            >
              <i className="ri-qr-scan-2-line text-lg" style={{ color: 'var(--primary)' }} />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 text-sm">Scan to Track</h3>
              <p className="text-gray-400 text-xs">QR code or barcode lookup</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors cursor-pointer"
          >
            <i className="ri-close-line" />
          </button>
        </div>

        {/* Mode switcher */}
        <div className="flex items-center gap-1 mx-5 mt-4 mb-1 bg-gray-100 rounded-xl p-1">
          <button
            onClick={() => switchMode('camera')}
            className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-xs font-semibold transition-all cursor-pointer ${
              mode === 'camera' ? 'bg-white text-gray-900' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <i className="ri-camera-line" />
            Use Camera
          </button>
          <button
            onClick={() => switchMode('upload')}
            className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-xs font-semibold transition-all cursor-pointer ${
              mode === 'upload' ? 'bg-white text-gray-900' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <i className="ri-image-line" />
            Upload Image
          </button>
        </div>

        {/* Content area */}
        <div className="px-5 pb-5 pt-3">

          {/* ── CAMERA MODE ── */}
          {mode === 'camera' && (
            <>
              {cameraError ? (
                <div className="py-4 px-2">
                  <div className="flex flex-col items-center text-center mb-5">
                    <div className="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center mb-3">
                      <i className="ri-camera-off-line text-2xl text-red-400" />
                    </div>
                    {cameraError === 'permission' && (
                      <>
                        <p className="text-gray-800 text-sm font-semibold mb-1">Camera Permission Denied</p>
                        <p className="text-gray-400 text-xs leading-relaxed mb-3">
                          Your browser blocked camera access. To fix this:
                        </p>
                        <ol className="text-left text-xs text-gray-500 space-y-1.5 mb-4">
                          <li className="flex gap-2"><span className="font-bold text-gray-700">1.</span> Click the camera/lock icon in your browser's address bar</li>
                          <li className="flex gap-2"><span className="font-bold text-gray-700">2.</span> Set Camera to "Allow"</li>
                          <li className="flex gap-2"><span className="font-bold text-gray-700">3.</span> Reload the page and try again</li>
                        </ol>
                      </>
                    )}
                    {cameraError === 'no_camera' && (
                      <>
                        <p className="text-gray-800 text-sm font-semibold mb-1">No Camera Found</p>
                        <p className="text-gray-400 text-xs leading-relaxed">
                          This device doesn't have a camera. Try the Upload Image option below instead.
                        </p>
                      </>
                    )}
                    {cameraError === 'generic' && (
                      <>
                        <p className="text-gray-800 text-sm font-semibold mb-1">Camera Unavailable</p>
                        <p className="text-gray-400 text-xs leading-relaxed">
                          Could not access camera. Use the Upload Image option or type your tracking number manually.
                        </p>
                      </>
                    )}
                  </div>
                  <button
                    onClick={() => switchMode('upload')}
                    className="w-full py-3 rounded-xl text-sm font-semibold text-white transition-opacity hover:opacity-90 cursor-pointer"
                    style={{ background: 'var(--primary)' }}
                  >
                    <i className="ri-image-line mr-2" />
                    Switch to Image Upload
                  </button>
                </div>
              ) : (
                <>
                  <div className="relative rounded-xl overflow-hidden bg-gray-900" style={{ minHeight: '260px' }}>
                    <div id="swifttrack-qr-reader" style={{ width: '100%' }} />
                    {!scanning && (
                      <div className="absolute inset-0 flex items-center justify-center bg-gray-900">
                        <div className="text-center">
                          <div className="w-10 h-10 border-2 border-teal-400 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
                          <p className="text-teal-400 text-xs">Starting camera...</p>
                        </div>
                      </div>
                    )}
                    {scanning && (
                      <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
                        <div className="relative w-48 h-48">
                          <div className="absolute top-0 left-0 w-7 h-7 border-t-2 border-l-2 border-teal-400 rounded-tl-md" />
                          <div className="absolute top-0 right-0 w-7 h-7 border-t-2 border-r-2 border-teal-400 rounded-tr-md" />
                          <div className="absolute bottom-0 left-0 w-7 h-7 border-b-2 border-l-2 border-teal-400 rounded-bl-md" />
                          <div className="absolute bottom-0 right-0 w-7 h-7 border-b-2 border-r-2 border-teal-400 rounded-br-md" />
                          <div
                            className="absolute left-1 right-1 h-0.5 bg-teal-400/80"
                            style={{ animation: 'scanLine 2s ease-in-out infinite', top: '50%' }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                  {scanning && (
                    <div className="flex items-center justify-center gap-2 mt-3 text-teal-600 text-xs">
                      <span className="w-1.5 h-1.5 bg-teal-500 rounded-full animate-ping" />
                      Scanning... aim at a QR code or barcode
                    </div>
                  )}
                </>
              )}
            </>
          )}

          {/* ── UPLOAD MODE ── */}
          {mode === 'upload' && (
            <div className="py-2">
              {/* Hidden container for html5-qrcode file scan */}
              <div id="swifttrack-file-scan-container" style={{ display: 'none' }} />

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={handleFileUpload}
              />

              <div
                onClick={() => !uploadScanning && fileInputRef.current?.click()}
                className={`rounded-xl border-2 border-dashed flex flex-col items-center justify-center gap-3 py-10 px-6 text-center transition-all ${
                  uploadScanning
                    ? 'border-gray-200 bg-gray-50 cursor-wait'
                    : 'border-gray-200 hover:border-teal-400/60 hover:bg-teal-50/30 cursor-pointer'
                }`}
                style={{ minHeight: '180px' }}
              >
                {uploadScanning ? (
                  <>
                    <div
                      className="w-10 h-10 border-2 border-t-transparent rounded-full animate-spin"
                      style={{ borderColor: 'var(--primary)', borderTopColor: 'transparent' }}
                    />
                    <p className="text-gray-500 text-sm">Reading QR code from image...</p>
                  </>
                ) : (
                  <>
                    <div
                      className="w-14 h-14 rounded-2xl flex items-center justify-center"
                      style={{ background: 'var(--primary-light)' }}
                    >
                      <i className="ri-image-add-line text-2xl" style={{ color: 'var(--primary)' }} />
                    </div>
                    <div>
                      <p className="text-gray-800 text-sm font-semibold mb-1">Take a photo or choose an image</p>
                      <p className="text-gray-400 text-xs">Tap to open camera or select a photo from your gallery containing the QR code</p>
                    </div>
                    <span
                      className="px-5 py-2 rounded-xl text-xs font-semibold text-white"
                      style={{ background: 'var(--primary)' }}
                    >
                      Select Image
                    </span>
                  </>
                )}
              </div>

              {uploadError && (
                <div className="mt-3 bg-red-50 border border-red-100 rounded-xl p-3 flex items-start gap-2">
                  <i className="ri-error-warning-line text-red-400 mt-0.5 flex-shrink-0" />
                  <p className="text-red-600 text-xs">{uploadError}</p>
                </div>
              )}

              <p className="text-gray-400 text-xs text-center mt-3">
                Works with any photo that clearly shows the QR code or barcode.
              </p>
            </div>
          )}

          {/* Footer hint */}
          <div className="mt-3 pt-3 border-t border-gray-100 text-center">
            <p className="text-gray-400 text-xs">
              Can't scan? Just type your tracking number directly in the search box.
            </p>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes scanLine {
          0% { transform: translateY(-80px); opacity: 0.8; }
          50% { opacity: 1; }
          100% { transform: translateY(80px); opacity: 0.8; }
        }
      `}</style>
    </div>
  );
}
